
import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format, subDays, addDays } from 'date-fns';
import DailyTile, { MealInfo } from '@/components/ui/daily-tile';
import { Recipe } from '@/types/recipe';
import { MealCompositionWithRecipes } from '@/types/mealComposition';
import { MealPlan } from '@/types/mealPlan';
import { MealNote } from '@/types/mealNote';

interface HomepageTileProps {
  date: Date;
  mealPlans: MealPlan[];
  mealNotes: MealNote[];
  recipes: Recipe[];
  mealCompositions: MealCompositionWithRecipes[];
  primaryMealType?: string; // For onboarding context
}

const HomepageTile: React.FC<HomepageTileProps> = ({
  date,
  mealPlans,
  mealNotes,
  recipes,
  mealCompositions,
  primaryMealType
}) => {
  const today = new Date();
  const yesterday = subDays(today, 1);
  const tomorrow = addDays(today, 1);
  const isYesterday = format(date, 'yyyy-MM-dd') === format(yesterday, 'yyyy-MM-dd');
  const isTomorrow = format(date, 'yyyy-MM-dd') === format(tomorrow, 'yyyy-MM-dd');
  
  // Convert meal plans to MealInfo format with meal type
  const mealPlanMeals: MealInfo[] = mealPlans.map(mealPlan => {
    if (mealPlan.recipeId) {
      const recipe = recipes.find(r => r.id === mealPlan.recipeId);
      if (recipe) {
        return {
          type: 'recipe' as const,
          id: recipe.id,
          name: recipe.title,
          isQuickRecipe: recipe.isQuickRecipe,
          prepTime: recipe.prepTime,
          cookTime: recipe.cookTime,
          mealType: mealPlan.mealType,
          authorFirstName: mealPlan.authorFirstName,
          imageUrl: recipe.image_url,
          storageImageUrl: recipe.storage_image_url,
          userImageUrl: recipe.user_image_url,
          storageImagePath: recipe.storage_image_path,
          userImagePath: recipe.user_image_path
        };
      }
    }
    
    if (mealPlan.mealCompositionId) {
      const composition = mealCompositions.find(c => c.id === mealPlan.mealCompositionId);
      if (composition) {
        return {
          type: 'composition' as const,
          id: composition.id,
          name: composition.name,
          recipeCount: composition.recipes.length,
          mealType: mealPlan.mealType,
          authorFirstName: mealPlan.authorFirstName
        };
      }
    }
    
    return null;
  }).filter(Boolean) as MealInfo[];

  // Convert meal notes to MealInfo format
  const noteMeals: MealInfo[] = mealNotes.map(note => ({
    type: 'note' as const,
    id: note.id,
    name: note.noteText,
    mealType: note.mealType as 'breakfast' | 'lunch' | 'dinner',
    authorFirstName: note.authorFirstName
  }));

  // Combine meal plans and notes
  const meals: MealInfo[] = [...mealPlanMeals, ...noteMeals];

  const hasActualMeals = mealPlanMeals.length > 0;
  const recipeCount = mealPlanMeals.filter(meal => meal.type === 'recipe').length;

  // Generate conditional footer actions and help text
  let footer = null;
  let helpText = null;
  
  if (isYesterday && hasActualMeals && recipeCount > 0) {
    // Show help text for any recipes (1 or more)
    helpText = (
      <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
        <MessageSquare size={14} />
        Leave reviews for yesterday's meals
      </div>
    );
  } else if (isYesterday && !hasActualMeals) {
    footer = (
      <Button variant="outline" size="sm" disabled className="w-full opacity-50">
        No Meals Selected
      </Button>
    );
  } else if (!hasActualMeals) {
    footer = (
      <Link to="/meal-planner" className="w-full">
        <Button variant="outline" size="sm" className="w-full">
          {isTomorrow ? "Plan Tomorrow's Meal" : "Plan Today's Meal"}
        </Button>
      </Link>
    );
  }

  return (
    <div>
      {helpText}
      <DailyTile
        date={date}
        meals={meals}
        footer={footer}
        showAllMealTypes={true}
        heightMode="flexible"
        showReviewButton={isYesterday && recipeCount > 0}
        isYesterday={isYesterday}
        primaryMealType={primaryMealType}
      />
    </div>
  );
};

export default HomepageTile;
